import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Zap,
  Banknote,
  PhoneCall,
  Monitor,
  CheckSquare,
  Store,
  ShoppingBag,
  Building2,
  Rocket,
  Stethoscope,
  Car,
  Activity,
  PieChart,
  Receipt,
  ScrollText,
  Lock,
  Check,
} from "lucide-react";
import Counter from "../components/Counter";

export default function HomePage() {
  return (
    <>
      <Hero />
      <PartnersMarquee />
      <Features />
      <StatsStrip />
      <Dashboard />
      <Industries />
      <Clients />
      <FinalCTA />
    </>
  );
}

/* ---------------------------------- HERO ---------------------------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div aria-hidden className="pointer-events-none absolute inset-0 grid-overlay opacity-70" />
      <div aria-hidden className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-emerald/10 blur-[120px]" />

      <div className="container-wide relative z-10 grid items-center gap-14 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-7">
          <span className="eyebrow fade-in">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald animate-pulse-dot" />
            Payment infrastructure · merchantcore.us
          </span>
          <h1
            className="mt-7 font-display text-[13vw] font-bold leading-[0.98] tracking-tight text-cloud sm:text-6xl lg:text-7xl"
            data-testid="hero-title"
          >
            Payments,
            <br />
            engineered to
            <br />
            <span className="text-gradient">get approved.</span>
          </h1>

          <p className="mt-8 max-w-xl font-sans text-base leading-relaxed text-muted sm:text-lg">
            MerchantCore is the payment platform for ambitious and high-risk
            merchants — high-approval accounts, next-day settlements, and a
            real-time dashboard. No hidden fees, no guesswork.
          </p>

          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/apply" className="btn-primary" data-testid="hero-cta-primary">
              Get started <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact-us" className="btn-secondary" data-testid="hero-cta-secondary">
              Free consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
            {["98.7% approval rate", "Next-day funding", "24/7 human support"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 font-sans text-sm text-muted">
                <Check className="h-4 w-4 text-emerald" /> {t}
              </span>
            ))}
          </div>
        </div>

        {/* Product-style dashboard card (matches brand aesthetic) */}
        <aside className="relative lg:col-span-5">
          <div className="card card-active p-0 overflow-hidden shadow-lift" data-testid="hero-dashboard-card">
            <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <span className="h-2 w-2 rounded-full bg-emerald animate-pulse-dot" />
                <span className="font-mono text-[11px] uppercase tracking-ultra text-muted">
                  Live dashboard
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-ultra text-faint">
                v2026.06
              </span>
            </div>

            <div className="p-6">
              <div className="font-mono text-[11px] uppercase tracking-ultra text-muted">
                Processed volume
              </div>
              <div className="mt-2 font-mono text-4xl font-bold tracking-tight text-cloud tabular-nums">
                $6,204,660
              </div>
              <div className="mt-1 inline-flex items-center gap-1.5 font-sans text-xs text-emerald">
                <ArrowUpRight className="h-3.5 w-3.5" /> +18.4% this month
              </div>

              {/* mini bar chart */}
              <div className="mt-6 flex h-24 items-end gap-1.5">
                {[42, 58, 47, 66, 54, 72, 63, 81, 70, 88, 79, 96].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-[3px] bg-gradient-to-t from-emerald/20 to-emerald/70"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <MiniStat k="Approval" v="98.7%" />
                <MiniStat k="Settle" v="T + 1" />
                <MiniStat k="Chargeback" v="0.42%" />
                <MiniStat k="Uptime" v="99.99%" />
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2">
                {["VISA", "MC", "AMEX", "DISC", "ACH", "APPLE"].map((x) => (
                  <div
                    key={x}
                    className="rounded-lg border border-line py-2 text-center font-mono text-[10px] font-semibold uppercase tracking-wide2 text-muted"
                  >
                    {x}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 -left-3 hidden rounded-full border border-emerald/40 bg-panel px-4 py-2 font-mono text-[10px] font-semibold uppercase tracking-ultra text-emerald shadow-glow-sm sm:block">
            Instant approval
          </div>
        </aside>
      </div>
    </section>
  );
}

function MiniStat({ k, v }) {
  return (
    <div className="rounded-xl border border-line bg-white/[0.02] px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-ultra text-faint">{k}</div>
      <div className="mt-1 font-mono text-lg font-semibold tracking-tight text-cloud">{v}</div>
    </div>
  );
}

/* ----------------------------- PARTNERS MARQUEE --------------------------- */

function PartnersMarquee() {
  const items = [
    "VISA", "MASTERCARD", "AMEX", "DISCOVER", "JCB", "UNIONPAY", "ACH",
    "APPLE PAY", "GOOGLE PAY", "PIX", "SEPA", "iDEAL", "ALIPAY", "WECHAT",
  ];
  const loop = [...items, ...items];
  return (
    <section className="border-y border-line bg-panel/40 py-8" data-testid="partners-marquee">
      <div className="container-wide mb-6 flex items-center justify-between">
        <span className="eyebrow">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Partners & integrations
        </span>
        <span className="hidden font-mono text-[11px] uppercase tracking-ultra text-faint sm:inline">
          Unlimited processing rails
        </span>
      </div>
      <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,#000_10%,#000_90%,transparent)]">
        <div className="marquee-track gap-10">
          {loop.map((x, i) => (
            <span key={`${x}-${i}`} className="flex shrink-0 items-center gap-10 font-display text-2xl font-semibold tracking-tight text-muted/70 sm:text-3xl">
              {x}
              <span className="text-emerald/60">◆</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- FEATURES -------------------------------- */

const FEATURES = [
  { n: "01", title: "No hidden fees", body: "Transparent pricing. What we quote is what you pay — no surprises, no fine print.", icon: ScrollText },
  { n: "02", title: "Instant approval", body: "Now offering instant onboarding. Start processing in hours, not weeks.", icon: Zap },
  { n: "03", title: "Online check acceptance", body: "Accept checks by web, phone, and fax with full reporting included.", icon: CheckSquare },
  { n: "04", title: "Next-day funding", body: "Visa, Mastercard, Discover and Amex deposited the next business day.", icon: Banknote },
  { n: "05", title: "Virtual terminal", body: "Phone, in-person, and mail order in one terminal, every channel, zero extra cost.", icon: Monitor },
  { n: "06", title: "24/7 human support", body: "Live, toll-free support staffed by specialists who know your industry.", icon: PhoneCall },
];

function Features() {
  return (
    <section id="solutions" className="section" data-testid="features-section">
      <div className="container-wide">
        <div className="grid items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> What's included
            </span>
            <h2 className="section-mega mt-6">
              The all-inclusive
              <br />
              <span className="text-gradient">merchant platform.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="body-text max-w-md">
              One contract, every channel, no upcharges. Everything below is
              standard with every MerchantCore account — across retail, online,
              and high-risk verticals.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ n, title, body, icon: Icon }) => (
            <article key={n} data-testid={`feature-card-${n}`} className="card card-hover group">
              <div className="flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-emerald/10 text-emerald transition-colors group-hover:bg-emerald/20">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="font-mono text-xs uppercase tracking-ultra text-faint">{n}</span>
              </div>
              <h3 className="mt-6 font-display text-xl font-semibold tracking-tight text-cloud">{title}</h3>
              <p className="mt-3 font-sans text-sm leading-relaxed text-muted">{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- STATS STRIP ------------------------------ */

function StatsStrip() {
  return (
    <section className="relative overflow-hidden" data-testid="stats-section">
      <div className="container-wide">
        <div className="relative overflow-hidden rounded-3xl border border-line bg-surface/60 p-10 sm:p-14">
          <div aria-hidden className="pointer-events-none absolute inset-0 grid-overlay opacity-60" />
          <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-20 h-[400px] w-[400px] rounded-full bg-emerald/10 blur-[110px]" />
          <div className="relative z-10 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <span className="eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> By the numbers
              </span>
              <h2 className="mt-6 section-title">
                Numbers don't
                <br />
                <span className="text-gradient">negotiate.</span>
              </h2>
              <p className="body-text mt-5">
                Cumulative performance across our merchant network — verified by
                our acquiring partners and updated daily.
              </p>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
                <Stat number={<Counter end={12500} suffix="+" />} label="Satisfied merchants" />
                <Stat number={<Counter end={48} prefix="$" suffix="M" />} label="Fees saved / year" />
                <Stat number={<Counter end={6} prefix="$" suffix="B+" />} label="Processed payments" />
                <Stat number={<Counter end={20} suffix="+" />} label="Acquiring banks" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div className="rounded-2xl border border-line bg-white/[0.02] p-6">
      <div className="font-mono text-4xl font-bold tracking-tight text-cloud tabular-nums sm:text-5xl">
        {number}
      </div>
      <div className="mt-3 font-mono text-[11px] uppercase tracking-ultra text-muted">{label}</div>
    </div>
  );
}

/* -------------------------------- DASHBOARD ------------------------------- */

const TILES = [
  { t: "Dashboard overview", icon: Activity },
  { t: "Reporting & analytics", icon: PieChart },
  { t: "Transaction history", icon: ScrollText },
  { t: "Purchase analytics", icon: ShoppingBag },
  { t: "Fraud & risk protection", icon: Lock },
  { t: "Orders & invoicing", icon: Receipt },
];

function Dashboard() {
  return (
    <section className="section" data-testid="dashboard-section">
      <div className="container-wide grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
          <span className="eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> The platform
          </span>
          <h2 className="section-title mt-6">
            Get paid every way.
            <br />
            Online. In person.
            <br />
            <span className="text-gradient">Everywhere.</span>
          </h2>
          <p className="body-text mt-6 max-w-md">
            A single operating layer for your entire payments stack — reporting,
            fraud, invoicing, and analytics under one roof.
          </p>
          <Link to="/contact-us" className="btn-primary mt-8" data-testid="dashboard-cta">
            See the platform <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {TILES.map(({ t, icon: Icon }, i) => (
              <div key={t} data-testid={`dashboard-tile-${i}`} className="card card-hover group">
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-ultra text-faint">
                    0{i + 1} / Module
                  </span>
                  <Icon className="h-5 w-5 text-muted transition-colors group-hover:text-emerald" />
                </div>
                <h3 className="mt-6 font-display text-lg font-semibold tracking-tight text-cloud">{t}</h3>
                <div className="mt-5 h-10 rounded-lg border border-line bg-grid-dark bg-[length:10px_10px]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- INDUSTRIES ------------------------------- */

const INDUSTRIES = [
  { name: "Retail", icon: Store, n: "01" },
  { name: "E-Commerce", icon: ShoppingBag, n: "02" },
  { name: "B2B", icon: Building2, n: "03" },
  { name: "Startups", icon: Rocket, n: "04" },
  { name: "TeleMed", icon: Stethoscope, n: "05" },
  { name: "Peptides", icon: Activity, n: "06" },
  { name: "Auto Dealership", icon: Car, n: "07" },
];

function Industries() {
  return (
    <section id="industries" className="section" data-testid="industries-section">
      <div className="container-wide">
        <div className="grid items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Industries
            </span>
            <h2 className="section-mega mt-6">
              We underwrite <span className="text-gradient">99%</span>
              <br />
              of industries — including:
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="body-text">
              From boutique retail to high-velocity SaaS and regulated TeleMed,
              our risk teams underwrite every vertical — even the ones other
              processors won't touch.
            </p>
          </div>
        </div>

        <ul className="mt-12 overflow-hidden rounded-2xl border border-line">
          {INDUSTRIES.map(({ name, icon: Icon, n }, i) => (
            <li
              key={name}
              data-testid={`industry-row-${n}`}
              className={`group flex items-center justify-between gap-6 px-6 py-7 transition-colors duration-200 hover:bg-emerald/10 sm:px-8 ${
                i !== INDUSTRIES.length - 1 ? "border-b border-line" : ""
              }`}
            >
              <div className="flex items-baseline gap-6 sm:gap-10">
                <span className="font-mono text-xs uppercase tracking-ultra text-faint">{n}</span>
                <span className="font-display text-2xl font-semibold tracking-tight text-cloud transition-colors group-hover:text-emerald sm:text-3xl">
                  {name}
                </span>
              </div>
              <div className="flex items-center gap-6">
                <Icon className="hidden h-5 w-5 text-muted sm:block" />
                <ArrowUpRight className="h-5 w-5 text-muted transition-all group-hover:text-emerald group-hover:rotate-[-12deg]" />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* --------------------------------- CLIENTS -------------------------------- */

function Clients() {
  const brands = ["ASTRA", "BRAVO", "RIDE", "PLANET", "FORTIS", "NORTHWIND", "BLOCKHAUS", "MERIDIAN"];
  return (
    <section className="section pt-0" data-testid="clients-section">
      <div className="container-wide">
        <div className="flex items-end justify-between border-b border-line pb-6">
          <span className="eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Our clients
          </span>
          <h2 className="font-display text-xl font-semibold tracking-tight text-cloud sm:text-2xl">
            We proudly represent
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-4">
          {brands.map((b) => (
            <div key={b} className="flex items-center justify-center bg-panel py-12">
              <span className="font-display text-xl font-semibold tracking-tight text-muted/70 sm:text-2xl">
                {b}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- FINAL CTA ------------------------------- */

function FinalCTA() {
  return (
    <section className="pb-24" data-testid="final-cta">
      <div className="container-wide">
        <div className="relative overflow-hidden rounded-3xl border border-emerald/30 bg-surface p-10 shadow-glow sm:p-16">
          <div aria-hidden className="pointer-events-none absolute inset-0 grid-overlay opacity-50" />
          <div aria-hidden className="pointer-events-none absolute -top-24 left-1/3 h-[360px] w-[360px] rounded-full bg-emerald/15 blur-[110px]" />
          <div className="relative z-10 grid items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <span className="eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Final call
              </span>
              <h2 className="mt-6 font-display text-4xl font-bold leading-[1.02] tracking-tight text-cloud sm:text-5xl lg:text-6xl">
                Unlock your cost savings
                <br />
                and custom solutions <span className="text-gradient">now.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="font-sans text-base leading-relaxed text-muted">
                Free, no-obligation savings proposal. Most merchants discover
                we're cheaper by the second statement comparison.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/apply" className="btn-primary" data-testid="final-cta-button">
                  Get started <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/about-us" className="btn-secondary">About us</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
